import { supabase } from '../lib/supabase';
import { OrganizationService } from './organization.service';

export interface Task {
  id: string;
  organization_id: string;
  title: string;
  description?: string;
  type?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
  assigned_by?: string;
  customer_id?: string;
  vehicle_id?: string;
  deal_id?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'todo' | 'review' | 'done';
  due_date?: string;
  reminder_date?: string;
  completed_at?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface CreateTaskData {
  title: string;
  description?: string;
  type?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
  customer_id?: string;
  vehicle_id?: string;
  deal_id?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'todo' | 'review' | 'done';
  due_date?: string;
  reminder_date?: string;
  tags?: string[];
}

export interface UpdateTaskData extends Partial<CreateTaskData> {
  id: string;
}

export class TasksService {
  /**
   * Get all tasks for the current user's organization
   */
  static async getTasks(): Promise<{ data: Task[] | null; error: any }> {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Get tasks for the organization
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return { data: null, error };
    }
  }

  /**
   * Get a single task by ID
   */
  static async getTask(taskId: string): Promise<{ data: Task | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error fetching task:', error);
      return { data: null, error };
    }
  }

  /**
   * Create a new task
   */
  static async createTask(taskData: CreateTaskData): Promise<{ data: Task | null; error: any }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Create the task
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...taskData,
          organization_id: organizationId,
          assigned_by: user.id
        })
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error creating task:', error);
      return { data: null, error };
    }
  }

  /**
   * Update an existing task
   */
  static async updateTask(taskData: UpdateTaskData): Promise<{ data: Task | null; error: any }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.organization_id) {
        return { data: null, error: { message: 'No organization found' } };
      }

      const { id, ...updateData } = taskData;

      // Update the task (RLS will ensure user can only update tasks in their organization)
      const { data, error } = await supabase
        .from('tasks')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('organization_id', userData.organization_id)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error updating task:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete a task
   */
  static async deleteTask(taskId: string): Promise<{ success: boolean; error: any }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: { message: 'Not authenticated' } };
      }

      // Get user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.organization_id) {
        return { success: false, error: { message: 'No organization found' } };
      }

      // Delete the task (RLS will ensure user can only delete tasks in their organization)
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)
        .eq('organization_id', userData.organization_id);

      if (error) {
        console.error('Error deleting task:', error);
        return { success: false, error };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting task:', error);
      return { success: false, error };
    }
  }

  /**
   * Delete multiple tasks
   */
  static async deleteTasks(taskIds: string[]): Promise<{ success: boolean; error: any; deletedCount?: number }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: { message: 'Not authenticated' } };
      }

      // Get user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.organization_id) {
        return { success: false, error: { message: 'No organization found' } };
      }

      // Delete the tasks (RLS will ensure user can only delete tasks in their organization)
      const { data, error } = await supabase
        .from('tasks')
        .delete()
        .in('id', taskIds)
        .eq('organization_id', userData.organization_id)
        .select('id');

      if (error) {
        console.error('Error deleting tasks:', error);
        return { success: false, error };
      }

      return {
        success: true,
        error: null,
        deletedCount: data?.length || 0
      };
    } catch (error) {
      console.error('Error deleting tasks:', error);
      return { success: false, error };
    }
  }

  /**
   * Get tasks by status
   */
  static async getTasksByStatus(status: string): Promise<{ data: Task[] | null; error: any }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.organization_id) {
        return { data: null, error: { message: 'No organization found' } };
      }

      // Get tasks for the organization with specific status
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('organization_id', userData.organization_id)
        .eq('status', status)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching tasks by status:', error);
      return { data: null, error };
    }
  }

  /**
   * Get tasks assigned to a specific user
   */
  static async getTasksByAssignee(assigneeId: string): Promise<{ data: Task[] | null; error: any }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.organization_id) {
        return { data: null, error: { message: 'No organization found' } };
      }

      // Get tasks for the organization assigned to specific user
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('organization_id', userData.organization_id)
        .eq('assigned_to', assigneeId)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching tasks by assignee:', error);
      return { data: null, error };
    }
  }

  /**
   * Mark task as completed
   */
  static async completeTask(taskId: string): Promise<{ data: Task | null; error: any }> {
    try {
      const { data, error } = await this.updateTask({
        id: taskId,
        status: 'completed',
        completed_at: new Date().toISOString()
      });

      return { data, error };
    } catch (error) {
      console.error('Error completing task:', error);
      return { data: null, error };
    }
  }

  /**
   * Reopen a completed task
   */
  static async reopenTask(taskId: string): Promise<{ data: Task | null; error: any }> {
    try {
      const { data, error } = await this.updateTask({
        id: taskId,
        status: 'pending',
        completed_at: undefined
      });

      return { data, error };
    } catch (error) {
      console.error('Error reopening task:', error);
      return { data: null, error };
    }
  }

  /**
   * Search tasks by title or description
   */
  static async searchTasks(query: string): Promise<{ data: Task[] | null; error: any }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.organization_id) {
        return { data: null, error: { message: 'No organization found' } };
      }

      // Search tasks using ilike for case-insensitive search
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('organization_id', userData.organization_id)
        .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error searching tasks:', error);
      return { data: null, error };
    }
  }
}

export default TasksService;